import {
  ApiOutlined,
  CheckCircleOutlined,
  CloseOutlined,
} from "@ant-design/icons";
import { Form, Select } from "antd";
import LicenseIcon from "../../../assets/images/vectors/SideBarIcons/LicenseIcon";
import CommonButton from "../../../global/components/CommonButton";
import {
  calculateTotals,
  iconMap,
  licenseOptions,
  orderTypeOptions,
} from "../../../utils/Common";
import LicenseTypeIcon from "../../../assets/images/vectors/LicenseTypeIcon";
import DoorTypeIcon from "../../../assets/images/vectors/DoorTypeIcon";
import SiteTypeIcon from "../../../assets/images/vectors/SiteTypeIcon";
import { useMemo } from "react";

/** When the user clears DealerNo after it was touched, ignore parent fallbacks (e.g. saved device dealer) for discount display. */
const useEffectiveDealerDiscountPercent = (form, dealerDiscountPercent) => {
  const watchedDealerNo = Form.useWatch("DealerNo", form);
  return useMemo(() => {
    if (!form) return dealerDiscountPercent;
    if (
      form.isFieldTouched("DealerNo") &&
      (watchedDealerNo === undefined ||
        watchedDealerNo === null ||
        watchedDealerNo === "")
    ) {
      return 0;
    }
    return dealerDiscountPercent;
  }, [form, dealerDiscountPercent, watchedDealerNo]);
};

/** Toggle rows 1–8 from Central `licenseOptions` (REST API … Badge). */
const LICENSE_CONFIRM_TOGGLE_LABELS = licenseOptions
  .filter((o) => Number(o.value) >= 1 && Number(o.value) <= 8)
  .map((o) => o.label);

const LICENSE_CONFIRM_DEVICE_KEYS = [
  "Channel",
  "Lockset",
  "OSDP Door",
  "Trilogy Lockset",
  "Unit",
  "Mobile Credential",
];

const LicenseConfirmForm = ({
  onConfirm,
  onCancel,
  mergedData,
  optionsData,
  licenseType,
  modelsOptions,
  siteTypeOptions,
  setLicenseType,
  licenseTypePrice,
  siteTypePrice = 0,
  doorPrice,
  form,
  loading,
  disableSelect,
  edit,
  originalLicenseData,
  dealerDiscountPercent = 0,
  isLicensePageSummary = false,
  hide = false,
}) => {
  const confirmTableRows = useMemo(
    () => [
      { kind: "siteType" },
      { kind: "model" },
      { kind: "door" },
      ...LICENSE_CONFIRM_TOGGLE_LABELS.map((label) => ({
        kind: "toggle",
        key: label,
      })),
      ...LICENSE_CONFIRM_DEVICE_KEYS.map((key) => ({
        kind: "device",
        key,
      })),
    ],
    [],
  );

  const totalPrice = calculateTotals(
    optionsData,
    licenseTypePrice,
    doorPrice,
    siteTypePrice,
  );
  const effectiveDealerDiscountPercent = useEffectiveDealerDiscountPercent(
    form,
    dealerDiscountPercent,
  );

  const normalizedDiscountPercent =
    Number.isFinite(Number(effectiveDealerDiscountPercent)) &&
    Number(effectiveDealerDiscountPercent) > 0
      ? Math.min(Number(effectiveDealerDiscountPercent), 100)
      : 0;

  const renderUnifiedLicensePriceTable = () => {
    const compareMode = Boolean(edit && originalLicenseData);
    let originalTotalVal = null;
    let diffVal = null;
    let discountedOldTotalVal = null;
    let discountedNewTotalVal = null;
    let discountedDiffVal = null;
    if (compareMode && originalLicenseData) {
      originalTotalVal = calculateTotals(
        originalLicenseData.optionsData,
        originalLicenseData.licenseTypePrice,
        originalLicenseData.doorPrice,
        originalLicenseData.siteTypePrice,
      );
      diffVal = Number(totalPrice) - Number(originalTotalVal);
      const factor =
        normalizedDiscountPercent > 0 ? 1 - normalizedDiscountPercent / 100 : 1;
      discountedOldTotalVal = Number(originalTotalVal) * factor;
      discountedNewTotalVal = Number(totalPrice) * factor;
      discountedDiffVal = discountedNewTotalVal - discountedOldTotalVal;
    }

    const fmtMoney = (v) => {
      if (v === "-" || v === null || v === undefined || v === "") return "-";
      const n = Number(v);
      if (!Number.isFinite(n)) return "-";
      return `$${n.toFixed(2)}`;
    };

    const isLifetime = licenseType === orderTypeOptions[1].value;

    const tableRows = isLifetime
      ? confirmTableRows.filter((row) => {
          if (row.kind === "siteType") {
            const newVal =
              siteTypeOptions?.find((m) => m.value === mergedData?.SiteType)
                ?.label ?? "";
            const oldVal = compareMode
              ? (siteTypeOptions?.find(
                  (m) => m.value === originalLicenseData?.SiteType,
                )?.label ?? "")
              : "";
            return compareMode
              ? newVal !== oldVal
              : Number(siteTypePrice ?? 0) > 0;
          }
          if (row.kind === "model") {
            return compareMode
              ? mergedData?.Model !== originalLicenseData?.Model
              : Number(licenseTypePrice ?? 0) > 0;
          }
          if (row.kind === "door") {
            return compareMode
              ? Number(mergedData?.Type) !== Number(originalLicenseData?.Type)
              : Number(doorPrice ?? 0) > 0;
          }
          if (row.kind === "toggle") {
            const curEnabled =
              optionsData[row.key]?.type === "toggle" &&
              optionsData[row.key]?.enabled;
            const origEnabled =
              originalLicenseData?.optionsData?.[row.key]?.type === "toggle" &&
              originalLicenseData?.optionsData?.[row.key]?.enabled;
            return compareMode ? curEnabled !== origEnabled : curEnabled;
          }
          if (row.kind === "device") {
            const curQty = Number(optionsData[row.key]?.quantity ?? 0);
            const origQty = Number(
              originalLicenseData?.optionsData?.[row.key]?.quantity ?? 0,
            );
            return compareMode ? curQty !== origQty : curQty > 0;
          }
          return true;
        })
      : confirmTableRows;

    /** Fixed slot + uniform SVG/ant icon size so every row lines up. */
    const licenseItemIcon = (node) => (
      <span
        className="inline-flex h-7 w-7 shrink-0 items-center justify-center text-gray-500 [&_.anticon]:inline-flex [&_.anticon]:h-4 [&_.anticon]:w-4 [&_.anticon]:items-center [&_.anticon]:justify-center [&_.anticon]:text-[15px] [&_svg]:h-4 [&_svg]:w-4 [&_svg]:shrink-0"
        aria-hidden
      >
        {node}
      </span>
    );
    const totalLabel =
      licenseType === orderTypeOptions[0].value
        ? "Monthly Price"
        : "Additional Price";

    return (
      <div className="mb-2 border border-gray-200 rounded-[4px] bg-white overflow-hidden">
        <div className="overflow-x-auto">
          <table
            className={`w-full text-xs table-fixed border-collapse ${
              isLicensePageSummary ? "min-w-[380px]" : "min-w-[650px]"
            }`}
          >
            <colgroup>
              {isLicensePageSummary ? (
                <>
                  <col style={{ width: "72%" }} />
                  <col style={{ width: "28%" }} />
                </>
              ) : (
                <>
                  <col style={{ width: "21%" }} />
                  <col style={{ width: "14%" }} />
                  <col style={{ width: "14%" }} />
                  <col style={{ width: "14%" }} />
                  <col style={{ width: "14%" }} />
                  <col style={{ width: "13%" }} />
                </>
              )}
            </colgroup>
            <thead>
              <tr className="bg-gray-50 border-b border-gray-200">
                <th className="px-2 py-1.5 text-left align-middle font-semibold text-gray-700">
                  License Item
                </th>
                {isLicensePageSummary ? (
                  <th className="px-2 py-1.5 text-left align-middle font-semibold text-gray-700 whitespace-nowrap">
                    Price
                  </th>
                ) : (
                  <>
                    <th className="px-2 py-1.5 text-left align-middle font-semibold text-gray-700">
                      Old License
                    </th>
                    <th className="px-2 py-1.5 text-left align-middle font-semibold text-gray-700 whitespace-nowrap">
                      Old Price
                    </th>
                    <th className="px-2 py-1.5 text-left align-middle font-semibold text-gray-700">
                      New License
                    </th>
                    <th className="px-2 py-1.5 text-left align-middle font-semibold text-gray-700 whitespace-nowrap">
                      New Price
                    </th>
                    <th className="px-2 py-1.5 pl-1.5 pr-1 text-left align-middle font-semibold text-gray-700 whitespace-nowrap">
                      Diff Price
                    </th>
                  </>
                )}
              </tr>
            </thead>
            <tbody>
              {tableRows.map((row, index) => {
                let licenseItem = "";
                let oldLic = "-";
                let newLic = "-";
                let oldPriceNum = null;
                let newPriceNum = null;
                let icon = null;

                if (row.kind === "siteType") {
                  licenseItem = "Site Type";
                  icon = licenseItemIcon(<SiteTypeIcon />);
                  const curSite = siteTypeOptions?.find(
                    (m) => m.value === mergedData?.SiteType,
                  );
                  newLic = curSite?.label ?? "N/A";
                  newPriceNum = Number(siteTypePrice ?? 0);
                  if (compareMode) {
                    const origSite = siteTypeOptions?.find(
                      (m) => m.value === originalLicenseData.SiteType,
                    );
                    oldLic = origSite?.label ?? "N/A";
                    oldPriceNum = Number(
                      originalLicenseData.siteTypePrice ?? 0,
                    );
                  }
                } else if (row.kind === "model") {
                  licenseItem = "Model";
                  icon = licenseItemIcon(<LicenseTypeIcon />);
                  const curModel = modelsOptions?.find(
                    (m) => m.value === mergedData?.Model,
                  );
                  newLic = curModel?.label ?? "N/A";
                  newPriceNum = Number(licenseTypePrice ?? 0);
                  if (compareMode) {
                    const origModel = modelsOptions?.find(
                      (m) => m.value === originalLicenseData.Model,
                    );
                    oldLic = origModel?.label ?? "N/A";
                    oldPriceNum = Number(
                      originalLicenseData.licenseTypePrice ?? 0,
                    );
                  }
                } else if (row.kind === "door") {
                  licenseItem = "Door";
                  icon = licenseItemIcon(<DoorTypeIcon />);
                  newLic =
                    mergedData?.Type != null && mergedData?.Type !== ""
                      ? String(mergedData.Type)
                      : "-";
                  newPriceNum = Number(doorPrice ?? 0);
                  if (compareMode) {
                    oldLic =
                      originalLicenseData?.Type != null &&
                      originalLicenseData?.Type !== ""
                        ? String(originalLicenseData.Type)
                        : "-";
                    oldPriceNum = Number(originalLicenseData.doorPrice ?? 0);
                  }
                } else if (row.kind === "toggle") {
                  const key = row.key;
                  licenseItem = key;
                  icon = licenseItemIcon(
                    iconMap[key] || <ApiOutlined className="!text-[15px]" />,
                  );
                  const curRaw = optionsData[key];
                  const origRaw = originalLicenseData?.optionsData?.[key];
                  const curItem =
                    curRaw?.type === "toggle"
                      ? curRaw
                      : { type: "toggle", enabled: false, price: 0 };
                  const origItem =
                    origRaw?.type === "toggle"
                      ? origRaw
                      : { type: "toggle", enabled: false, price: 0 };
                  newLic = curItem.enabled ? "Yes" : "No";
                  newPriceNum = curItem.enabled
                    ? Number(curItem.price ?? 0)
                    : 0;
                  if (compareMode) {
                    oldLic = origItem.enabled ? "Yes" : "No";
                    oldPriceNum = origItem.enabled
                      ? Number(origItem.price ?? 0)
                      : 0;
                  }
                } else if (row.kind === "device") {
                  const key = row.key;
                  licenseItem = key;
                  icon = licenseItemIcon(
                    iconMap[key] || <ApiOutlined className="!text-[15px]" />,
                  );
                  const curRaw = optionsData[key];
                  const origRaw = originalLicenseData?.optionsData?.[key];
                  const curItem =
                    curRaw &&
                    (curRaw.type === "input" || curRaw.type === "select")
                      ? curRaw
                      : { type: "input", quantity: 0, price: 0 };
                  const origItem =
                    origRaw &&
                    (origRaw.type === "input" || origRaw.type === "select")
                      ? origRaw
                      : { type: "input", quantity: 0, price: 0 };
                  newLic = String(curItem.quantity ?? 0);
                  newPriceNum = Number(curItem.price ?? 0);
                  if (compareMode) {
                    oldLic = String(origItem.quantity ?? 0);
                    oldPriceNum = Number(origItem.price ?? 0);
                  }
                }

                const diffNum =
                  compareMode &&
                  oldPriceNum != null &&
                  newPriceNum != null &&
                  Number.isFinite(oldPriceNum) &&
                  Number.isFinite(newPriceNum)
                    ? newPriceNum - oldPriceNum
                    : null;

                const oldPriceCell = compareMode ? fmtMoney(oldPriceNum) : "-";
                const newPriceCell = fmtMoney(newPriceNum);
                const diffCell =
                  compareMode && diffNum != null && Number.isFinite(diffNum) ? (
                    <span
                      className={`font-semibold ${
                        diffNum > 0
                          ? "text-green-600"
                          : diffNum < 0
                            ? "text-red-600"
                            : "text-gray-600"
                      }`}
                    >
                      {diffNum > 0 ? "+" : diffNum < 0 ? "-" : ""}$
                      {Math.abs(diffNum).toFixed(2)}
                    </span>
                  ) : (
                    "-"
                  );

                return (
                  <tr
                    key={`${row.kind}-${row.key ?? index}`}
                    className="border-b border-gray-200 hover:bg-gray-50"
                  >
                    <td className="px-2 align-middle">
                      <div className="flex min-h-[28px] items-center gap-2.5">
                        {icon}
                        <span className="min-w-0 flex-1 leading-snug text-gray-800 break-words">
                          {licenseItem}
                        </span>
                      </div>
                    </td>
                    {isLicensePageSummary ? (
                      <td className="px-2 align-middle text-left font-medium leading-snug text-gray-900 whitespace-nowrap">
                        {newPriceCell}
                      </td>
                    ) : (
                      <>
                        <td className="px-2 align-middle leading-snug text-gray-700">
                          {oldLic}
                        </td>
                        <td className="px-2 align-middle text-left leading-snug text-gray-600">
                          {oldPriceCell}
                        </td>
                        <td className="px-2 align-middle font-medium leading-snug text-gray-900">
                          {newLic}
                        </td>
                        <td className="px-2 align-middle text-left font-medium leading-snug text-gray-900">
                          {newPriceCell}
                        </td>
                        <td className="px-2 align-middle text-left whitespace-nowrap">
                          {diffCell}
                        </td>
                      </>
                    )}
                  </tr>
                );
              })}
            </tbody>
            <tfoot className="border-t-2 border-gray-300">
              {isLicensePageSummary ? (
                <tr className="bg-gray-50">
                  <td className="px-2 py-1.5 text-left align-middle font-semibold text-gray-800">
                    {totalLabel}
                  </td>
                  <td className="px-2 py-1.5 text-left align-middle font-bold whitespace-nowrap text-gray-900">
                    ${Number(totalPrice).toFixed(2)}
                  </td>
                </tr>
              ) : compareMode && originalTotalVal != null ? (
                <>
                  <tr className="bg-gray-50">
                    <td className="px-2 pt-1 text-left align-middle font-semibold text-gray-800">
                      {totalLabel}
                    </td>
                    <td className="px-2 align-middle" />
                    <td className="px-2 text-left align-middle font-bold whitespace-nowrap text-gray-800">
                      {licenseType !== orderTypeOptions[1].value
                        ? `$${Number(originalTotalVal).toFixed(2)}`
                        : ""}
                    </td>
                    <td className="px-2 align-middle" />
                    <td className="px-2 text-left align-middle font-bold whitespace-nowrap text-gray-900">
                      {licenseType !== orderTypeOptions[1].value
                        ? `$${Number(totalPrice).toFixed(2)}`
                        : ""}
                    </td>
                    <td
                      className={`px-2 text-left align-middle font-bold whitespace-nowrap ${
                        diffVal > 0
                          ? "text-green-600"
                          : diffVal < 0
                            ? "text-red-600"
                            : "text-gray-600"
                      }`}
                    >
                      {isLifetime
                        ? `$${Math.max(diffVal, 0).toFixed(2)}`
                        : `${diffVal > 0 ? "+" : diffVal < 0 ? "-" : ""}$${Math.abs(diffVal).toFixed(2)}`}
                    </td>
                  </tr>
                  {normalizedDiscountPercent > 0 && Math.max(diffVal, 0) > 0 ? (
                    <tr className="bg-white font-semibold text-red-600">
                      <td className="px-2 py-1.5 text-left align-middle break-words leading-snug">
                        Discounted Price ({normalizedDiscountPercent}%)
                      </td>
                      <td className="px-2 py-1.5 align-middle" />
                      <td className="px-2 py-1.5 text-left align-middle whitespace-nowrap">
                        {isLifetime
                          ? ""
                          : `$${discountedOldTotalVal.toFixed(2)}`}
                      </td>
                      <td className="px-2 py-1.5 align-middle" />
                      <td className="px-2 py-1.5 text-left align-middle whitespace-nowrap">
                        {isLifetime
                          ? ""
                          : `$${discountedNewTotalVal.toFixed(2)}`}
                      </td>
                      <td className="px-2 py-1.5 text-left align-middle whitespace-nowrap">
                        {discountedDiffVal < 0 ? "-" : ""}$
                        {Math.abs(discountedDiffVal).toFixed(2)}
                      </td>
                    </tr>
                  ) : null}
                </>
              ) : (
                <tr className="bg-gray-50">
                  <td className="px-2 py-1.5 text-left align-middle font-semibold text-gray-800">
                    {totalLabel}
                  </td>
                  <td className="px-2 py-1.5 align-middle text-gray-400">-</td>
                  <td className="px-2 py-1.5 align-middle text-gray-400">-</td>
                  <td className="px-2 py-1.5 align-middle text-gray-400">-</td>
                  <td className="px-2 py-1.5 text-left align-middle font-bold whitespace-nowrap text-gray-900">
                    ${Number(totalPrice).toFixed(2)}
                  </td>
                  <td className="px-2 py-1.5 align-middle text-gray-400">-</td>
                </tr>
              )}
            </tfoot>
          </table>
        </div>
      </div>
    );
  };

  const onReset = () => {
    onCancel();
  };

  const buttonsData = [
    {
      label: "Apply",
      icon: <CheckCircleOutlined />,
      onClick: onConfirm,
      loading: loading,
    },
    {
      label: "Cancel",
      icon: <CloseOutlined />,
      onClick: onReset,
    },
  ];

  return (
    <div className="bg-white rounded-[4px] overflow-hidden flex flex-col max-h-[90vh]">
      {/* Header with License Type Selector */}
      <div className="py-2 bg-gray-200 py-1 px-2.5 rounded-[4px] flex items-center justify-between mb-1 flex-shrink-0">
        <div className="flex items-center">
          <LicenseIcon />
          <h2 className="text-sm font-[Poppins] text-black ml-2">
            License Price
          </h2>
        </div>
        {/* {!hide && (
          <div className="w-24">
            <Select
              value={licenseType}
              onChange={(value) => {
                setLicenseType(value);
                form.setFieldValue("LicenseType", value);
              }}
              disabled={disableSelect}
              options={orderTypeOptions}
              size="small"
              style={{ width: "100%", height: "30px" }}
            />
          </div>
        )} */}
      </div>

      {/* Scrollable Content */}
      <div className="flex-1 overflow-y-auto">
        {renderUnifiedLicensePriceTable()}
      </div>

      <div className="flex-shrink-0 border-t border-gray-200 bg-white pt-1">
        <div className="flex justify-center items-center py-1">
          <CommonButton buttonsData={buttonsData} />
        </div>
      </div>
    </div>
  );
};

export default LicenseConfirmForm;
